'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import BannerArt from '@/components/BannerArt';
import Footer from '@/components/Footer';
import ScrollRevealInit from '@/app/components/ScrollRevealInit';

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

// Only set for basePath-deployed builds (the /demo/clients/... static export); empty in production.
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || '';

const perks = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Collaborative Team',
    desc: 'Work alongside passionate people who care about quality and nutrition.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    title: 'Growth Opportunities',
    desc: 'Be part of a fast-growing food brand making a difference in Indian kitchens.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
      </svg>
    ),
    title: 'Work-Life Balance',
    desc: 'We believe in sustainable work practices that keep our team energised and motivated.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
      </svg>
    ),
    title: 'Meaningful Mission',
    desc: 'Contribute to a brand that champions clean, nutritious food for every Indian family.',
  },
];

export default function CareerClient() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', message: '' });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCvFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cvFile) {
      setError('Please attach your CV.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('name', form.name);
      data.append('email', form.email);
      data.append('phone', form.phone);
      data.append('message', form.message);
      data.append('cv', cvFile);

      const res = await fetch(`${BASE_PATH}/api/career`, { method: 'POST', body: data });
      if (!res.ok) {
        const resData = await res.json().catch(() => null);
        throw new Error(resData?.error || 'Something went wrong. Please try again.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'var(--card)',
    border: '1px solid var(--border)',
    color: 'var(--foreground)',
    borderRadius: '0.75rem',
    padding: '0.875rem 1rem',
    width: '100%',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  return (
    <main className="grain-overlay bg-background min-h-screen overflow-x-hidden">
      <Header />

      {/* Page Hero */}
      <section
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #2A1015 0%, #3A1820 55%, #2A1015 100%)' }}>
        <BannerArt />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="block text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--accent)' }}>
            Join Our Team
          </span>
          <h1
            className="font-display font-[200] text-white leading-none mb-6"
            style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', letterSpacing: '0.02em' }}>
            Career
          </h1>
          <p className="text-white/60 text-lg max-w-xl leading-relaxed">
            Be part of a mission-driven food brand that&apos;s redefining nutrition for Indian households.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="py-16 md:py-24" style={{ background: 'var(--secondary)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
              Why Kasturi
            </span>
            <h2
              className="font-display font-[200]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
              Why Work With Us
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {perks.map((p, i) => (
              <div
                key={p.title}
                className={`p-6 rounded-2xl reveal stagger-${i + 1}`}
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}>
                  {p.icon}
                </div>
                <h3 className="font-display font-[200] text-base mb-2" style={{ color: 'var(--foreground)' }}>{p.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 md:py-28" style={{ background: 'var(--background)' }}>
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
              Apply Now
            </span>
            <h2
              className="font-display font-[200] mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
              Let&apos;s Grow{' '}
              <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>Together</span>
            </h2>
            <p className="text-muted-foreground text-sm">
              Send us your details and we&apos;ll get back to you about exciting opportunities.
            </p>
          </div>

          {submitted ? (
            <div
              className="reveal text-center p-10 rounded-2xl"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="font-display font-[200] text-xl mb-2" style={{ color: 'var(--foreground)' }}>Application Received!</h3>
              <p className="text-muted-foreground text-sm">Thank you for your interest. We&apos;ll review your application and get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="reveal space-y-5" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '1.5rem', padding: '2rem' }}>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Upload Your CV *
                </label>
                <div
                  className="relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors"
                  style={{ borderColor: cvFile ? 'var(--primary)' : 'var(--border)', background: cvFile ? 'var(--secondary)' : 'transparent' }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    required
                  />
                  {cvFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-sm font-medium" style={{ color: 'var(--accent-ink)' }}>{cvFile.name}</span>
                    </div>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Click to upload or drag &amp; drop</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>PDF, DOC, DOCX accepted</p>
                    </>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about yourself and the role you're interested in..."
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
              {error && (
                <p className="text-sm" style={{ color: 'var(--highlight)' }}>{error}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}>
                {submitting ? 'Sending...' : 'Send Application'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <ScrollRevealInit />
    </main>
  );
}
